import React, { useEffect, useState } from 'react';
import { Link, router } from '@inertiajs/react';
import Layout from '../../../components/Layout.jsx';

function ConfirmModal({
  open,
  title,
  message,
  confirmLabel,
  confirmTone = 'danger',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  busy = false,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape' && !busy) onCancel?.();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const confirmClasses = confirmTone === 'danger'
    ? 'bg-red-600 hover:bg-red-700 focus-visible:ring-red-500'
    : 'bg-indigo-600 hover:bg-indigo-700 focus-visible:ring-indigo-500';

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-gray-600/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel?.();
      }}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h3 id="confirm-modal-title" className="text-lg font-semibold text-gray-900">
          {title}
        </h3>
        {message && <p className="mt-2 text-sm text-gray-600">{message}</p>}
        <div className="mt-5 flex gap-3 justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer ${confirmClasses}`}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = status === 'open'
    ? 'bg-green-50 text-green-700 border-green-200'
    : 'bg-gray-50 text-gray-600 border-gray-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${styles}`}>
      {status === 'open' ? 'Open' : 'Closed'}
    </span>
  );
}

function MessageBubble({ message }) {
  const fromStaff = message.from_staff;
  return (
    <div className={`flex ${fromStaff ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
        fromStaff ? 'bg-indigo-50 border border-indigo-100' : 'bg-gray-50 border border-gray-100'
      }`}>
        <div className="flex items-center gap-2 mb-1">
          <p className={`text-xs font-semibold ${fromStaff ? 'text-indigo-700' : 'text-gray-700'}`}>
            {fromStaff ? `${message.author_name} (Support)` : message.author_name}
          </p>
          <span className="text-xs text-gray-400">·</span>
          <p className="text-xs text-gray-400">{message.created_at}</p>
        </div>
        <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">{message.body}</p>
      </div>
    </div>
  );
}

export default function SuperadminSupportTicketShow({ ticket }) {
  const [reply, setReply] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [statusSubmitting, setStatusSubmitting] = useState(false);

  const handleReply = (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSubmitting(true);
    router.post(`/superadmin/support_tickets/${ticket.id}/messages`, { message: { body: reply.trim() } }, {
      onFinish: () => setSubmitting(false),
      onSuccess: () => setReply(''),
    });
  };

  const requestStatusChange = (next) => setPendingStatus(next);
  const cancelStatusChange = () => {
    if (statusSubmitting) return;
    setPendingStatus(null);
  };

  const confirmStatusChange = () => {
    if (!pendingStatus) return;
    setStatusSubmitting(true);
    router.patch(`/superadmin/support_tickets/${ticket.id}`, { status: pendingStatus }, {
      onFinish: () => {
        setStatusSubmitting(false);
        setPendingStatus(null);
      },
    });
  };

  const isClosing = pendingStatus === 'closed';
  const modalCopy = isClosing
    ? {
        title: 'Close this ticket?',
        message: "The user won't be able to reply unless support reopens it (or until they send a new message, which auto-reopens the ticket).",
        confirmLabel: 'Close ticket',
        confirmTone: 'danger',
      }
    : {
        title: 'Reopen this ticket?',
        message: 'The user will be able to reply again, and it will return to the open queue.',
        confirmLabel: 'Reopen ticket',
        confirmTone: 'primary',
      };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-6 sm:p-8 space-y-6">
        <div>
          <Link href="/superadmin/support_tickets" className="text-sm font-semibold text-indigo-600 hover:text-indigo-700">
            ← Back to support inbox
          </Link>
          <div className="mt-2 flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <h1 className="text-2xl font-bold tracking-tight text-gray-900 break-words">{ticket.subject}</h1>
              <p className="text-sm text-gray-600 mt-1">
                <span className="font-medium">{ticket.user.name}</span> · {ticket.user.email}
              </p>
              <p className="text-xs text-gray-500 mt-1">Opened {ticket.created_at}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <StatusBadge status={ticket.status} />
              {ticket.status === 'open' ? (
                <button
                  type="button"
                  onClick={() => requestStatusChange('closed')}
                  className="inline-flex items-center justify-center px-3 py-2 text-sm font-semibold rounded-lg text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition cursor-pointer"
                >
                  Close ticket
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => requestStatusChange('open')}
                  className="inline-flex items-center justify-center px-3 py-2 text-sm font-semibold rounded-lg text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 transition cursor-pointer"
                >
                  Reopen
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-4">
          {ticket.messages.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">No messages yet.</p>
          ) : (
            ticket.messages.map((m) => <MessageBubble key={m.id} message={m} />)
          )}
        </div>

        <form onSubmit={handleReply} className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-3">
          <label htmlFor="staff_reply" className="block text-sm font-semibold text-gray-700">
            Reply as support team
          </label>
          <textarea
            id="staff_reply"
            value={reply}
            rows={5}
            maxLength={10000}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Write a reply to the user…"
            className="block w-full rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none transition resize-y"
          />
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-gray-400">
              {ticket.status === 'closed' ? 'Replying will reopen this ticket.' : 'Visible to the user.'}
            </p>
            <button
              type="submit"
              disabled={submitting || !reply.trim()}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition cursor-pointer"
            >
              {submitting ? 'Sending…' : 'Send reply'}
            </button>
          </div>
        </form>
      </div>

      <ConfirmModal
        open={pendingStatus !== null}
        title={modalCopy.title}
        message={modalCopy.message}
        confirmLabel={modalCopy.confirmLabel}
        confirmTone={modalCopy.confirmTone}
        onConfirm={confirmStatusChange}
        onCancel={cancelStatusChange}
        busy={statusSubmitting}
      />
    </Layout>
  );
}
